import { Fragment, useMemo, useState } from 'react'
import GcpCopyButton from '@/components/gcp/GcpCopyButton'
import {
  classifyTriggerChip,
  classifyWastePatternChip,
  formatUsd,
  getQueryOptimizationHint,
} from '@/components/gcp/gcpHelpers'
import type { GcpRecurringQueryGroup } from '@/services/gcpCosts'

interface GcpQueryGroupsTableProps {
  groups: GcpRecurringQueryGroup[]
  unpartitionedTables: string[]
}

function formatBytes(bytes: number): string {
  if (bytes >= 1024 ** 4) return `${(bytes / 1024 ** 4).toFixed(2)} TB`
  if (bytes >= 1024 ** 3) return `${(bytes / 1024 ** 3).toFixed(1)} GB`
  if (bytes >= 1024 ** 2) return `${(bytes / 1024 ** 2).toFixed(1)} MB`
  return `${bytes} B`
}

export default function GcpQueryGroupsTable({
  groups,
  unpartitionedTables,
}: GcpQueryGroupsTableProps) {
  const [expandedFingerprint, setExpandedFingerprint] = useState<string | null>(null)

  const sortedGroups = useMemo(
    () =>
      [...groups]
        .filter((group) => group.estimated_monthly_cost >= 0.01)
        .sort((a, b) => b.estimated_monthly_cost - a.estimated_monthly_cost),
    [groups]
  )

  if (sortedGroups.length === 0) {
    return <p className="text-sm text-gray-500">No recurring query groups found in this window.</p>
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full table-fixed divide-y divide-gray-200 text-sm">
        <thead className="bg-gray-50">
          <tr>
            <th className="w-[7rem] px-3 py-2 text-left font-medium text-gray-600">Est. $/mo</th>
            <th className="w-[8rem] px-3 py-2 text-left font-medium text-gray-600">Cadence & runs</th>
            <th className="w-[9rem] px-3 py-2 text-left font-medium text-gray-600">Trigger</th>
            <th className="px-3 py-2 text-left font-medium text-gray-600">Query preview</th>
            <th className="w-[7rem] px-3 py-2 text-right font-medium text-gray-600">Avg cost/run</th>
            <th className="w-[10rem] px-3 py-2 text-left font-medium text-gray-600">Who/what</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {sortedGroups.map((group) => {
            const expanded = expandedFingerprint === group.fingerprint
            const trigger = classifyTriggerChip(group.trigger, group.trigger_label)
            const hint = getQueryOptimizationHint(group, unpartitionedTables)
            const fullSql = group.full_query_sample ?? group.query_preview ?? ''
            const preview = (group.query_preview ?? '').slice(0, 80)

            return (
              <Fragment key={group.fingerprint}>
                <tr
                  className="cursor-pointer align-top hover:bg-gray-50"
                  onClick={() =>
                    setExpandedFingerprint(expanded ? null : group.fingerprint)
                  }
                >
                  <td className="px-3 py-2 text-gray-900">
                    {formatUsd(group.estimated_monthly_cost)}
                  </td>
                  <td className="px-3 py-2 text-gray-800">
                    <div>{group.cadence_label} · {group.run_count} runs</div>
                    {(group.waste_patterns ?? []).length > 0 ? (
                      <div className="mt-1 flex flex-wrap gap-1">
                        {(group.waste_patterns ?? []).map((pattern) => {
                          const chip = classifyWastePatternChip(pattern.pattern)
                          return (
                            <span
                              key={pattern.pattern}
                              className={`inline-flex rounded px-1.5 py-0.5 text-xs font-medium ${chip.colorClass}`}
                            >
                              {chip.label}
                            </span>
                          )
                        })}
                      </div>
                    ) : null}
                  </td>
                  <td className="px-3 py-2 text-gray-800">{trigger.label}</td>
                  <td className="max-w-md px-3 py-2 text-gray-800 whitespace-normal break-words">
                    {preview}
                    {(group.query_preview ?? '').length > 80 ? '…' : ''}
                  </td>
                  <td className="px-3 py-2 text-right text-gray-800">
                    {formatUsd(group.avg_cost_per_run)}
                  </td>
                  <td className="px-3 py-2 text-gray-800">
                    <div>{group.user_email ?? '—'}</div>
                    {group.destination_table ? (
                      <div className="text-xs text-gray-500">→ {group.destination_table}</div>
                    ) : null}
                  </td>
                </tr>
                {expanded ? (
                  <tr>
                    <td colSpan={6} className="bg-gray-50 px-3 py-3">
                      <div className="space-y-3">
                        <div className="flex items-start justify-between gap-3">
                          <p className="text-sm font-medium text-gray-900">Full SQL</p>
                          <GcpCopyButton text={fullSql} label="Copy SQL" />
                        </div>
                        <pre className="max-h-48 overflow-auto rounded-md border border-gray-200 bg-white p-3 text-sm leading-relaxed text-gray-800 whitespace-pre-wrap">
                          {fullSql || 'No query text available.'}
                        </pre>

                        <div className="grid gap-3 sm:grid-cols-2">
                          <div>
                            <p className="text-xs font-medium uppercase text-gray-500">
                              Destination table
                            </p>
                            <p className="text-sm text-gray-800">
                              {group.destination_table ?? '—'}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs font-medium uppercase text-gray-500">
                              Referenced tables
                            </p>
                            <p className="text-sm text-gray-800">
                              {(group.top_referenced_tables ?? []).join(', ') || '—'}
                            </p>
                          </div>
                        </div>

                        <div>
                          <p className="mb-1 text-xs font-medium uppercase text-gray-500">
                            Last runs
                          </p>
                          <div className="overflow-x-auto">
                            <table className="min-w-full text-sm">
                              <thead>
                                <tr className="text-left text-gray-500">
                                  <th className="pr-3 py-1">Time</th>
                                  <th className="pr-3 py-1">Bytes</th>
                                  <th className="pr-3 py-1">Cost</th>
                                  <th className="py-1">Job ID</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(group.run_samples ?? []).map((run, index) => (
                                  <tr key={`${run.job_id ?? index}`} className="text-gray-800">
                                    <td className="pr-3 py-1">
                                      {run.creation_time
                                        ? new Date(run.creation_time).toLocaleString()
                                        : '—'}
                                    </td>
                                    <td className="pr-3 py-1">
                                      {run.total_bytes_processed != null
                                        ? formatBytes(run.total_bytes_processed)
                                        : '—'}
                                    </td>
                                    <td className="pr-3 py-1">
                                      {formatUsd(run.estimated_cost_usd)}
                                    </td>
                                    <td className="py-1">{run.job_id ?? '—'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>

                        <p className="rounded-md border border-blue-100 bg-blue-50 px-3 py-2 text-sm text-blue-900">
                          {hint}
                        </p>

                        {(group.waste_patterns ?? []).length > 0 ? (
                          <div className="space-y-3">
                            <p className="text-sm font-medium text-gray-900">
                              Waste patterns
                            </p>
                            {(group.waste_patterns ?? []).map((pattern) => (
                              <div
                                key={pattern.pattern}
                                className="rounded-md border border-amber-100 bg-amber-50 px-3 py-3"
                              >
                                <div className="mb-2 flex flex-wrap items-center gap-2">
                                  <span
                                    className={`inline-flex rounded px-1.5 py-0.5 text-xs font-medium ${classifyWastePatternChip(pattern.pattern).colorClass}`}
                                  >
                                    {classifyWastePatternChip(pattern.pattern).label}
                                  </span>
                                  <span className="text-xs text-amber-900">
                                    Est. savings{' '}
                                    {formatUsd(pattern.estimated_savings_usd_monthly)}/mo
                                  </span>
                                </div>
                                <p className="text-sm text-amber-950">
                                  {pattern.explanation_plain}
                                </p>
                                <p className="mt-1 text-sm text-amber-900">
                                  {pattern.suggested_fix}
                                </p>
                                <div className="mt-2 flex items-start justify-between gap-3">
                                  <pre className="max-h-40 flex-1 overflow-auto rounded border border-amber-200 bg-white p-2 text-xs text-gray-800 whitespace-pre-wrap">
                                    {pattern.fix_command_or_sql}
                                  </pre>
                                  <GcpCopyButton
                                    text={pattern.fix_command_or_sql}
                                    label="Copy fix"
                                  />
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ) : null}
              </Fragment>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
